import { CoinFlightHud } from "../ui/overlay/coin_flight_hud.js";
import { uiEffectsEnabled } from "../graphics/graphics_quality.js";
import { Game } from "../game.js";
import { isNetworkishError } from "../utils/network_errors.js";
import { Screen } from "./screen.js";
import { preloadAdsInBackground } from "../ads/first_load_manager.js";
import { audioController } from "../audio/audio_controller.js";
import { AudioClips } from "../audio/audio_constants.js";
import { WebGLUIRuntime } from "../webgl_2d/webgl_ui_runtime.js";
import { Vfx2DRuntime } from "../webgl_2d/vfx_2d_runtime.js";
import { LevelPackService, OfflineLevelUnavailableError } from "../level_pack/level_pack_service.js";
import { WinStreakTowerService } from "../win_streak_tower/win_streak_tower_service.js";
import { ResourceManager } from "../resource_loader/resource_manager.js";
import { EventsConfigService, GameEventID } from "../events_config/events_config_service.js";
import { PrizeShotService } from "../prize_shot/prize_shot_service.js";
import { NetworkManager } from "../network_manager.js";
import { LeaderboardBoardService } from "../api/leaderboard/leaderboard_board_service.js";
import { Vfx2DManager } from "../webgl_2d/vfx_2d_manager.js";
import { Vector2 } from "../common/vector2.js";
import { collectDomImageUrls, decodeDomImageUrls, getDomHost, resolveUiBackend, resyncDomHost, showDomHostForRaster } from "../ui/dom_backend/dom_backend.js";
import { ImagesHolder } from "../image_loader/images_holder.js";
import { isMemoryConstrained } from "../utils/memory_policy.js";
import { IReusableScreen, isReusableScreen } from "./reusable_screen.js";
import { runWhenIdle } from "../utils/idle_task.js";
import { BackgroundWork } from "../utils/background_work.js";
import { DialogManager } from "../ui/dialog/dialog_manager.js";
import { OverlayManager } from "../ui/overlay/overlay_manager.js";
import { StartupImagePlan } from "../image_loader/startup_image_plan.js";
import { StartupProgress } from "../ui/screens/loading_screen/startup_progress.js";

export enum Screens
{
  GAME = "game",
  MENU = "menu",
  SHOP = "shop",
  LEADERBOARD = "leaderboard",
  DAILY_TASKS = "daily_tasks",
  FEEDBACK = "feedback",
  ADVENTURE = "adventure",
  DAILY_CHALLENGE = "daily_challenge",
  CHAIN_OFFER = "chain_offer",
  ONE_PLUS_ONE_OFFER = "one_plus_one_offer",
  ONE_PLUS_TWO_OFFER = "one_plus_two_offer",
  START_OFFER = "start_offer",
  CHAIN_VIDEO = "chain_video",
}

const PREWARM_DISABLE_KEY = "dom_prewarm_off";
const DOM_SCREEN_URLS_KEY = "bb_dom_screen_urls_v1";
const DOM_DECODE_GATE_TIMEOUT_MS = 300;
const DOM_NAVIGATION_URLS = "navigation";
const PREWARM_START_DELAY_MS = 2500;
const PREFETCH_IDLE_TIMEOUT_MS = 400;
const MENU_PREBUILD_DELAY_MS = 1000;
const MENU_RASTER_OPACITY = 0.002;

function safeLocalStorageGet( key: string ): string | null
{
  try { return localStorage.getItem( key ); }
  catch { return null; }
}

export class ScreenManager
{
  private static canvases: Record<Screens, HTMLCanvasElement[]> = {} as any;

  private static current_screen: Screen | null = null;
  private static current_ui: any;
  private static current_screen_name: Screens | null = null;
  private static change_listeners: Array<( next: Screens | null ) => void> = [];
  private static after_next_go_back_callback: (() => void) | null = null;

  private static needed_navigation_block: Record<Screens, boolean> = {} as any;
  private static readonly COIN_IMPACT_TAIL_MS = 250;

  static registerCanvas( name: Screens, canvas: HTMLCanvasElement | null, is_navigation_block: boolean ): void
  {
    if ( !canvas )
      throw new Error( `[ScreenManager] Canvas is missing for screen "${name}".` );

    if ( !this.canvases[name] )
      this.canvases[name] = [];

    this.canvases[name].push( canvas );
    canvas.style.display = "none";

    this.needed_navigation_block[name] = is_navigation_block;
  }

  static registerMenuScreenCanvases( canvases: ( HTMLCanvasElement | null )[], is_navigation_block: boolean ): void
  {
    for ( const canvas of canvases )
    {
      if ( canvas )
      {
        this.registerCanvas( Screens.MENU, canvas, is_navigation_block );
      }
    }
  }

  static registerGameScreenCanvases( canvases: ( HTMLCanvasElement | null )[], is_navigation_block: boolean = false ): void
  {
    for ( const canvas of canvases )
    {
      if ( canvas )
      {
        this.registerCanvas( Screens.GAME, canvas, is_navigation_block );
      }
    }
  }

  public static isMenuTab( s: Screens | null | undefined ): boolean
  {
    return s === Screens.MENU || s === Screens.SHOP;
  }

  public static isNavigationTab( screen: Screens | null | undefined ): boolean
  {
    return screen === Screens.MENU || screen === Screens.SHOP || screen === Screens.LEADERBOARD;
  }

  static getCurrentUi(): any
  {
    return this.current_ui ?? null;
  }

  static getCurrentScreen(): Screens | null
  {
    return this.current_screen_name;
  }

  static getCurrentScreenInstance(): Screen | null
  {
    return this.current_screen;
  }

  /** Sends a complete tap directly to the currently active canvas UI. */
  public static replayTapOnCurrentUi( p: Vector2, source_canvas?: HTMLCanvasElement ): void
  {
    this.forwardTouchToCurrentUi( "start", p, source_canvas );
    this.forwardTouchToCurrentUi( "end", p, source_canvas );
  }

  /**
   * Hands one touch phase of a canvas lying on top (navigation, tooltips) to the active screen,
   * in the screen canvas' coordinates: for touches on the top canvas' transparent parts.
   */
  public static forwardTouchToCurrentUi( phase: "start" | "move" | "end", p: Vector2, source_canvas?: HTMLCanvasElement ): void
  {
    const ui = this.current_ui;
    if ( !ui || typeof ui.onTouchStart !== "function" || typeof ui.onTouchEnd !== "function" )
      return;

    let target_p = p;
    const target_canvas = typeof ui.getContext === "function"
      ? ui.getContext()?.canvas as HTMLCanvasElement | undefined
      : undefined;
    if ( source_canvas && target_canvas )
    {
      const source_rect = source_canvas.getBoundingClientRect();
      const target_rect = target_canvas.getBoundingClientRect();
      target_p = new Vector2(
        source_rect.left + p.x - target_rect.left,
        source_rect.top + p.y - target_rect.top
      );
    }

    const now = performance.now();
    if ( phase === "start" ) ui.onTouchStart( target_p, now );
    else if ( phase === "end" ) ui.onTouchEnd( target_p, now );
    else if ( typeof ui.onTouchMove === "function" ) ui.onTouchMove( target_p, now );
  }

  static addOnChangeListener( listener: ( next: Screens | null ) => void ): void
  {
    this.change_listeners.push( listener );
  }

  private static shouldKeepPreviousInstance( next: Screens ): boolean
  {
    return next === Screens.FEEDBACK;
  }

  static setAfterNextGoBackCallback( callback: (() => void) | null ): void
  {
    this.after_next_go_back_callback = callback;
  }

  private static notifyScreenChanged(): void
  {
    for ( const listener of this.change_listeners )
    {
      try { listener( this.current_screen_name ); }
      catch ( e ) { console.warn( '[ScreenManager] change listener failed', e ); }
    }
  }

  static hideAll( preserve_vfx: boolean = false )
  {
    if ( !preserve_vfx )
    {
      WebGLUIRuntime.clearAll()
      Vfx2DRuntime.clearAll();
      CoinFlightHud.hide();
    }
    
    for ( const canvas_list of Object.values( this.canvases ) )
    {
      for ( const canvas of canvas_list )
      {
        canvas.style.display = "none";
      }
    }

    for ( const canvas of this.canvases[Screens.CHAIN_VIDEO] ?? [] )
      Game.instance.releaseScreenCanvas( canvas );
  }

  private static loading_owner_gen: number | null = null;
  private static latest_request_name: Screens | null = null;

  private static isStale( gen: number ): boolean
  {
    if ( gen === this.switch_generation )
      return false;

    if ( this.loading_owner_gen === gen )
    {
      if ( this.latest_request_name === Screens.GAME )
        this.loading_owner_gen = this.switch_generation;
      else
      {
        this.loading_owner_gen = null;
        Game.instance.hideLoadingGameScreen();
      }
    }
    return true;
  }

  public static isSwitching(): boolean
  {
    return this.switch_in_flight > 0;
  }

  private static switch_in_flight = 0;

  /** A tap back on the visible tab cancels a different tab still preparing its first frame. */
  public static cancelPendingTabSwitch(): void
  {
    if ( !this.isNavigationTab( this.current_screen_name ) || !this.isNavigationTab( this.latest_request_name ) )
      return;
    this.switch_generation++;
    this.latest_request_name = this.current_screen_name;
  }

  static async showScreen( name: Screens, opts?: any )
  {
    const gen = ++this.switch_generation;
    this.latest_request_name = name;
    this.switch_in_flight++;
    try
    {
      await this.showScreenRequest( gen, name, opts );
    }
    finally
    {
      this.switch_in_flight--;
    }
  }

  private static async showScreenRequest( gen: number, name: Screens, opts?: any )
  {
    // Feeds the startup loading bar; a no-op once the first screen is up.
    const progress = ( step: Parameters<typeof StartupProgress.screenStage>[1] ) =>
      StartupProgress.screenStage( name === Screens.GAME, step );
    progress( 'request' );

    // The menu is intentionally usable while startup network data loads, but
    // a level must not be constructed until its release is ready. Otherwise
    // the first entry uses the bundled layout and a repeated entry uses the
    // server layout for the very same level number.
    // A Prize Shot round runs on the GAME screen but needs no level release.
    const prize_shot = name === Screens.GAME && opts?.prize_shot === true;
    if ( prize_shot && !PrizeShotService.isActive() )
    {
      if ( this.getCurrentScreen() === Screens.MENU )
        return;
      name = Screens.MENU;
      opts = undefined;
    }

    if ( name === Screens.GAME && !prize_shot )
    {
      await LevelPackService.waitForStartupReady();
      if ( this.isStale( gen ) )
        return;
      progress( 'startup_ready' );
    }

    const gated_event: GameEventID | null =
      name === Screens.DAILY_CHALLENGE ? "daily_challenge"
        : name === Screens.DAILY_TASKS ? "daily_tasks"
          : name === Screens.CHAIN_OFFER ? "chain_offer"
            : name === Screens.ADVENTURE ? "win_streak_tower"
              : null;
    if ( gated_event && !EventsConfigService.isEventUnlocked( gated_event ) )
    {
      if ( this.getCurrentScreen() === Screens.MENU )
        return;
      name = Screens.MENU;
      opts = undefined;
    }

    if ( name === Screens.ADVENTURE )
    {
      if ( opts?.tower_state_preloaded !== true )
      {
        const cached = WinStreakTowerService.getCached();
        const use_cached = WinStreakTowerService.isShowable( cached ) && !WinStreakTowerService.isPeriodOver();
        const payload = use_cached ? cached : await WinStreakTowerService.fetch( true );
        if ( this.isStale( gen ) || !WinStreakTowerService.isShowable( payload ) )
          return;
        if ( use_cached )
          void WinStreakTowerService.fetch( true );
        opts = { ...opts, tower_state: payload.tower };
      }
    }

    if ( name === Screens.GAME && !prize_shot && !opts?.is_challenge && !NetworkManager.isOnline()
      && LevelPackService.isActive() && !LevelPackService.isLevelCached( opts?.level ?? 1 ) )
    {
      NetworkManager.showOfflineLevelsExhausted();
      return;
    }

    if ( name === Screens.LEADERBOARD && !NetworkManager.isOnline() )
    {
      NetworkManager.showOfflineLeaderboard();
      return;
    }

    // The board request starts on the tap, alongside loading the screen's art, not after it:
    // building the screen first took ~0.7 s on a cold open before a request even went out.
    if ( name === Screens.LEADERBOARD )
      LeaderboardBoardService.refreshIfStale().catch( () => { /* the screen reports it */ } );

    // A level-to-level transition is still a full game-screen rebuild, so it
    // needs the loading canvas just as much as a menu-to-game transition.
    if ( name === Screens.GAME )
    {
      if ( !Game.instance.isLoadingGameScreenShown() )
      {
        Game.instance.showLoadingGameScreen( opts?.preserve_vfx === true, prize_shot ? 'prize_shot' : 'default' );
        Game.instance.holdSceneRevealUntil( Vfx2DManager.getLatestCoinFlyFinishTime() + ScreenManager.COIN_IMPACT_TAIL_MS );
        this.loading_owner_gen = gen;
        await Game.instance.waitForLoadingGameScreenPaint();
        if ( this.isStale( gen ) )
          return;
        progress( 'loader_painted' );
      }
      else if ( this.loading_owner_gen === null )
        this.loading_owner_gen = gen;
    }

    try
    {
      if ( name === Screens.GAME )
      {
        // Goal icons, the result overlay and its tournament block are loaded
        // after startup (StartupImagePlan); a level must not start without them.
        await Promise.all( [ ResourceManager.whenReady(), StartupImagePlan.whenReady( StartupImagePlan.GAME_PRIORITY ) ] );
        if ( this.isStale( gen ) )
          return;
        progress( 'resources_ready' );
      }

      if ( name === Screens.GAME && !prize_shot && !opts?.is_challenge )
      {
        try
        {
          await LevelPackService.ensureLevel( opts?.level ?? 1 );
          if ( this.isStale( gen ) )
            return;
          progress( 'level_ready' );
        }
        catch ( e )
        {
          if ( this.isStale( gen ) )
            return;
          console.warn( "[ScreenManager] level fetch failed, staying on the current screen", e );
          if ( !( e instanceof OfflineLevelUnavailableError ) && isNetworkishError( e ) )
            NetworkManager.handleNetworkError( e );
          NetworkManager.showOfflineLevelsExhausted();
          if ( this.loading_owner_gen === gen )
            await this.showScreenContent( this.current_screen_name ?? Screens.MENU, undefined, gen );
          return;
        }
      }

      await this.showScreenContent( name, opts, gen );
      progress( 'content_shown' );
    }
    finally
    {
      if ( this.loading_owner_gen === gen )
      {
        this.loading_owner_gen = null;
        await Game.instance.waitForSceneRevealed();
        progress( 'scene_revealed' );
        await Game.instance.waitForMinimumLoadingGameScreenDuration();
        Game.instance.hideLoadingGameScreen();
        progress( 'loader_hidden' );
      }
    }
  }

  private static pooled_screens = new Map<Screens, IReusableScreen>();

  private static canPoolScreen( name: Screens, ui: unknown ): ui is IReusableScreen
  {
    return name !== Screens.GAME
      && resolveUiBackend( name ) === "dom"
      && isReusableScreen( ui )
      && ui.isDomMode();
  }

  private static destroyUi( ui: any ): void
  {
    if ( typeof ui.destroy === "function" )
      ui.destroy();

    if ( typeof ui.cleanup === "function" )
      ui.cleanup();
  }

  private static reuseOrCreate<T>( name: Screens, opts: unknown, create: () => T, redraw = true ): T
  {
    const pooled = this.pooled_screens.get( name );
    if ( !pooled )
      return create();

    this.pooled_screens.delete( name );
    let reusable = false;
    try
    {
      reusable = pooled.canReuseScreen( opts );
    }
    catch ( e )
    {
      console.warn( `[ScreenManager] canReuseScreen failed for ${name}`, e );
    }

    if ( reusable )
    {
      pooled.setDomHidden( false, redraw );
      pooled.onScreenShown( opts );
      return pooled as unknown as T;
    }

    this.destroyUi( pooled );
    return create();
  }

  private static dropPooledScreensForGame(): void
  {
    if ( !isMemoryConstrained() )
      return;
    for ( const [ name, ui ] of [ ...this.pooled_screens ] )
    {
      if ( name === Screens.MENU )
        continue;
      this.pooled_screens.delete( name );
      this.destroyUi( ui );
    }
    this.prewarm_scheduled = false;
  }

  public static dropPooledScreens(): void
  {
    for ( const ui of this.pooled_screens.values() )
      this.destroyUi( ui );
    this.pooled_screens.clear();
  }

  public static pooledScreenNames(): Screens[]
  {
    return [ ...this.pooled_screens.keys() ];
  }

  private static prewarm_scheduled = false;
  private static readonly PREWARM_ORDER: Screens[] = [ Screens.SHOP, Screens.LEADERBOARD, Screens.ADVENTURE, Screens.DAILY_TASKS, Screens.DAILY_CHALLENGE, Screens.CHAIN_OFFER ];

  public static schedulePrewarm(): void
  {
    if ( this.prewarm_scheduled || safeLocalStorageGet( PREWARM_DISABLE_KEY ) === "1" )
      return;
    this.prewarm_scheduled = true;
    const screens = uiEffectsEnabled() ? this.PREWARM_ORDER : this.PREWARM_ORDER.filter( name => this.isNavigationTab( name ) );
    setTimeout( () => { void this.prewarmPool( screens ); }, PREWARM_START_DELAY_MS );
  }

  private static async createUiFor( name: Screens ): Promise<unknown>
  {
    switch ( name )
    {
      case Screens.SHOP: return Game.instance.initializeShopScreen();
      case Screens.LEADERBOARD: return Game.instance.initializeLeaderboardScreen();
      case Screens.DAILY_TASKS: return Game.instance.initializeDailyTasksScreen();
      case Screens.DAILY_CHALLENGE: return Game.instance.initializeDailyChallengeScreen();
      case Screens.CHAIN_OFFER: return Game.instance.initializeChainOfferScreen();
      case Screens.ADVENTURE:
      {
        const payload = await WinStreakTowerService.fetch( true );
        if ( !payload?.enabled || !payload.tower || payload.tower.floors.length < 2 )
          return null;
        return Game.instance.initializeAdventureScreen( payload.tower );
      }
      default: return null;
    }
  }

  private static prewarmAllowed(): boolean
  {
    return this.current_screen_name === Screens.MENU
      && !DialogManager.isAnyDialogOpened()
      && !OverlayManager.isAnyOverlayOpened();
  }

  private static switch_generation = 0;
  private static raster_prewarm_canvases: HTMLCanvasElement[] = [];

  private static endRasterPrewarm( next: Screens ): void
  {
    if ( this.raster_prewarm_canvases.length === 0 )
      return;
    for ( const canvas of this.raster_prewarm_canvases )
      resyncDomHost( canvas );
    this.raster_prewarm_canvases = [];
    if ( next !== Screens.MENU )
      this.pooled_screens.get( Screens.MENU )?.setDomHidden( true );
  }

  public static prebuildMenu(): void
  {
    if ( isMemoryConstrained() )
      return;

    const generation = this.switch_generation;
    const build_at = performance.now() + MENU_PREBUILD_DELAY_MS;
    const still_on_game = () => generation === this.switch_generation && this.current_screen_name === Screens.GAME;

    runWhenIdle( () =>
    {
      void ( async () =>
      {
        await Game.instance.prepareScreenImages( Screens.MENU );
        if ( resolveUiBackend( Screens.MENU ) !== "dom" )
          return;
        await this.decodeDomScreenImages( Screens.MENU );
        await new Promise<void>( resolve => setTimeout( resolve, Math.max( 0, build_at - performance.now() ) ) );
        // Built while the level plays: a slot keeps the ~130 ms build (CPU x4) out of a shot.
        await BackgroundWork.run( "menu prebuild", () => this.prebuildMenuNow( still_on_game ) );
      } )();
    }, PREFETCH_IDLE_TIMEOUT_MS );
  }

  private static prebuildMenuNow( still_on_game: () => boolean ): void
  {
    if ( !still_on_game() )
      return;

    const stale = this.pooled_screens.get( Screens.MENU );
    if ( stale )
    {
      this.pooled_screens.delete( Screens.MENU );
      this.destroyUi( stale );
    }

    const ui = Game.instance.initializeMenuScreen( true );
    if ( !this.canPoolScreen( Screens.MENU, ui ) )
    {
      this.destroyUi( ui );
      return;
    }
    ui.render();
    ui.onScreenHidden();
    this.pooled_screens.set( Screens.MENU, ui );
    this.raster_prewarm_canvases = this.canvases[Screens.MENU] ?? [];
    for ( const canvas of this.raster_prewarm_canvases )
      showDomHostForRaster( canvas, MENU_RASTER_OPACITY );
  }

  public static async prewarmPool( names: Screens[] ): Promise<void>
  {
    for ( const name of names )
    {
      if ( resolveUiBackend( name ) !== "dom" )
        return;
      if ( this.pooled_screens.has( name ) || this.current_screen_name === name )
        continue;
      if ( !this.prewarmAllowed() )
        return;

      // Decoding the art and building the screen are two slots: each is a burst of raster and main-thread work.
      // The slot can come seconds later, when the player has left the menu.
      const prepared = await BackgroundWork.run( `screen images ${name}`, async () =>
      {
        if ( !this.prewarmAllowed() )
          return false;
        await Game.instance.prepareScreenImages( name );
        return true;
      } );
      if ( !prepared )
        return;
      const release = await BackgroundWork.acquire( `screen prewarm ${name}` );
      try
      {
        if ( !this.prewarmAllowed() || this.pooled_screens.has( name ) )
          return;
        this.prewarmOne( name, await this.createUiForPrewarm( name ) );
      }
      finally
      {
        release();
      }
    }
  }

  private static async createUiForPrewarm( name: Screens ): Promise<unknown>
  {
    try { return await this.createUiFor( name ); }
    catch ( e ) { console.warn( `[ScreenManager] prewarm of ${name} failed`, e ); return null; }
  }

  private static prewarmOne( name: Screens, ui: unknown ): void
  {
    if ( !ui )
      return;
    if ( !this.canPoolScreen( name, ui ) || this.pooled_screens.has( name ) || this.current_screen_name === name )
    {
      this.destroyUi( ui );
      return;
    }
    ui.render?.();
    ui.onScreenHidden();
    ui.setDomHidden( true );
    this.pooled_screens.set( name, ui );
  }

  private static pooledIsReusable( name: Screens, opts: unknown ): boolean
  {
    const pooled = this.pooled_screens.get( name );
    if ( !pooled )
      return false;
    try { return pooled.canReuseScreen( opts ); }
    catch { return false; }
  }

  private static dom_screen_urls: Record<string, string[]> | null = null;

  private static domScreenUrls(): Record<string, string[]>
  {
    if ( this.dom_screen_urls === null )
    {
      try { this.dom_screen_urls = JSON.parse( safeLocalStorageGet( DOM_SCREEN_URLS_KEY ) ?? "{}" ) ?? {}; }
      catch { this.dom_screen_urls = {}; }
    }
    return this.dom_screen_urls as Record<string, string[]>;
  }

  private static collectHostUrls( canvases: HTMLCanvasElement[] ): Set<string>
  {
    const urls = new Set<string>();
    for ( const canvas of canvases )
      collectDomImageUrls( getDomHost( canvas ), urls );
    return urls;
  }

  private static rememberDomScreenUrls(): void
  {
    const saved = this.domScreenUrls();
    let changed = false;
    const remember = ( key: string, urls: Set<string> ): void =>
    {
      const list = [ ...urls ].filter( url => !url.startsWith( "blob:" ) && !url.startsWith( "data:" ) ).sort();
      if ( list.length === 0 || list.join( "|" ) === ( saved[key] ?? [] ).join( "|" ) )
        return;
      saved[key] = list;
      changed = true;
    };

    const name = this.current_screen_name;
    if ( name !== null && resolveUiBackend( name ) === "dom" && this.canvases[name] )
      remember( name, this.collectHostUrls( this.canvases[name] ) );

    const nav = document.getElementById( "navigationCanvas" ) as HTMLCanvasElement | null;
    if ( nav && resolveUiBackend( DOM_NAVIGATION_URLS ) === "dom" )
      remember( DOM_NAVIGATION_URLS, this.collectHostUrls( [ nav ] ) );

    if ( changed )
    {
      try { localStorage.setItem( DOM_SCREEN_URLS_KEY, JSON.stringify( saved ) ); }
      catch {}
    }
  }

  private static async decodeDomScreenImages( name: Screens ): Promise<void>
  {
    const saved = this.domScreenUrls();
    const urls = new Set<string>( saved[name] ?? [] );
    for ( const url of ImagesHolder.getScreenUrls( name ) )
      urls.add( url );
    if ( this.canvases[name] )
      for ( const url of this.collectHostUrls( this.canvases[name] ) )
        urls.add( url );
    if ( this.needed_navigation_block[name] )
    {
      for ( const url of saved[DOM_NAVIGATION_URLS] ?? [] )
        urls.add( url );
      const nav = document.getElementById( "navigationCanvas" ) as HTMLCanvasElement | null;
      if ( nav )
        for ( const url of this.collectHostUrls( [ nav ] ) )
          urls.add( url );
    }
    await decodeDomImageUrls( urls, DOM_DECODE_GATE_TIMEOUT_MS );
  }

  private static timingEnabled(): boolean
  {
    return safeLocalStorageGet( "perf_screen_timing" ) === "1";
  }

  private static async showScreenContent( name: Screens, opts?: any, gen: number = this.switch_generation ): Promise<void>
  {
    const reuse_pooled = this.pooledIsReusable( name, opts );
    // A valid bottom tab already owns its DOM and loaded art. Reveal it in this call;
    // onScreenShown below still refreshes balances, offers and leaderboard data.
    const fast_tab = reuse_pooled
      && this.isNavigationTab( this.current_screen_name ) && this.isNavigationTab( name )
      && ImagesHolder.areScreensReady( [ name ] );
    const timing = this.timingEnabled() ? { t0: performance.now(), images: 0, decode: 0, build: 0 } : null;

    if ( !fast_tab )
    {
      if ( reuse_pooled && !this.isNavigationTab( name ) )
        void Game.instance.prepareScreenImages( name );
      else
        await Game.instance.prepareScreenImages( name );
      const decode_t0 = performance.now();
      if ( resolveUiBackend( name ) === "dom" )
      {
        this.rememberDomScreenUrls();
        await this.decodeDomScreenImages( name );
      }
      if ( timing ) { timing.images = performance.now() - timing.t0; timing.decode = performance.now() - decode_t0; }
      await new Promise<void>( resolve => setTimeout( resolve, 0 ) );
    }
    if ( gen !== this.switch_generation )
      return;
    this.endRasterPrewarm( name );
    this.hideAll( opts?.preserve_vfx === true );

    Game.instance.setGameSceneCanvasVisible( name === Screens.GAME );

    const ui_canvases = this.canvases[name];

    if ( ui_canvases )
    {
      for ( const canvas of ui_canvases )
      {
        Game.instance.allocateScreenCanvas( canvas );
        canvas.style.display = "block";
      }
    }

    if ( this.current_screen )
    {
      this.current_screen.destroy();
      this.current_screen = null;
    }

    if ( this.current_ui )
    {
      const prev_name = this.current_screen_name;
      if ( prev_name !== null && prev_name !== name && this.canPoolScreen( prev_name, this.current_ui ) )
      {
        const ui = this.current_ui as IReusableScreen;
        ui.onScreenHidden();
        ui.setDomHidden( true );
        this.pooled_screens.set( prev_name, ui );
      }
      else
      {
        this.destroyUi( this.current_ui );
      }

      this.current_ui = null;
    }

    // Set the active screen name before syncing the navigation bar: its
    // updateScreenBlock() reads getCurrentScreen(), so it must already reflect
    // the destination or the bar keeps highlighting the previous tab (e.g. a
    // dialog redirecting to the shop left "Menu" selected).
    this.current_screen_name = name;

    if ( name === Screens.GAME )
      this.dropPooledScreensForGame();
    else if ( name === Screens.MENU && isMemoryConstrained() )
      this.schedulePrewarm();

    Game.instance.updateNavigationBlock();

    if ( this.needed_navigation_block[name] )
    {
      Game.instance.showNavigationBlock();
    } else
    {
      Game.instance.hideNavigationBlock();
    }

    if ( name === Screens.GAME )
    {
      const { screen, ui } = opts?.prize_shot === true
        ? Game.instance.initializePrizeShotScreen()
        : Game.instance.initializeGameScreen();

      this.current_screen = screen;
      this.current_ui = ui;
    }

    if ( name === Screens.MENU )
    {
      this.current_ui = this.reuseOrCreate( name, opts, () => Game.instance.initializeMenuScreen(), !fast_tab );
      preloadAdsInBackground();
    }

    if ( name === Screens.SHOP )
    {
      this.current_ui = this.reuseOrCreate( name, opts, () => Game.instance.initializeShopScreen(), !fast_tab );
    }

    if ( name === Screens.LEADERBOARD )
    {
      this.current_ui = this.reuseOrCreate( name, opts, () => Game.instance.initializeLeaderboardScreen(), !fast_tab );
    }

    if ( name === Screens.DAILY_TASKS )
    {
      this.current_ui = this.reuseOrCreate( name, opts, () => Game.instance.initializeDailyTasksScreen() );
    }

    if ( name === Screens.FEEDBACK )
    {
      this.current_ui = Game.instance.initializeFeedbackScreen();
    }

    if ( name === Screens.ADVENTURE )
    {
      this.current_ui = this.reuseOrCreate( name, opts, () => Game.instance.initializeAdventureScreen( opts.tower_state ) );
    }

    if ( name === Screens.DAILY_CHALLENGE )
    {
      this.current_ui = this.reuseOrCreate( name, opts, () => Game.instance.initializeDailyChallengeScreen() );
    }

    if ( name === Screens.CHAIN_OFFER )
    {
      this.current_ui = this.reuseOrCreate( name, opts, () => Game.instance.initializeChainOfferScreen() );
    }

    if ( name === Screens.ONE_PLUS_ONE_OFFER )
    {
      this.current_ui = Game.instance.initializeOnePlusOneOfferScreen( opts );
    }

    if ( name === Screens.ONE_PLUS_TWO_OFFER )
    {
      this.current_ui = Game.instance.initializeOnePlusTwoOfferScreen( opts );
    }

    if ( name === Screens.CHAIN_VIDEO )
    {
      this.current_ui = Game.instance.initializeChainVideoScreen();
    }

    if ( name === Screens.START_OFFER )
    {
      this.current_ui = Game.instance.initializeStartOfferScreen();
    }

    Game.instance.releaseScreenImages( name );

    if ( timing )
    {
      timing.build = performance.now() - timing.t0 - timing.images;
      console.info( `[ScreenManager] ${name}: images ${timing.images.toFixed( 0 )} ms (decode ${timing.decode.toFixed( 0 )} ms), build ${timing.build.toFixed( 0 )} ms, pooled ${reuse_pooled}` );
    }

    this.notifyScreenChanged();
    const level = opts?.level ?? 1;
    void audioController.playMusic(
      name === Screens.GAME
        ? ( level % 4 === 0 ? AudioClips.MUSIC_4 : AudioClips.MUSIC_3 )
        : AudioClips.MUSIC_2,
      true
    );

    if ( !this.current_screen )
      return;

    await this.current_screen.init( {
      level: opts?.level ?? 1,
      is_challenge: opts?.is_challenge ?? false,
      level_start_booster_id: opts?.level_start_booster_id,
      defer_level_start_booster: opts?.defer_level_start_booster === true,
    } );

    // Keep the loading canvas above the scene until the initialized game UI
    // has produced a real frame. Otherwise the WebGL clear/background can be
    // exposed for one or more frames between init() and the render loop.
    if ( name === Screens.GAME )
      await Game.instance.waitForNextGameUIPaint();

    // Do not await: startup effects must remain visible after the loading
    // canvas is removed. The screen owns input blocking while this runs.
    void this.current_screen.afterFirstPaint?.();
  }

  static showPreparedScreen(
    name: Screens,
    screen?: Screen,
    ui?: any
  )
  {
    this.hideAll();

    Game.instance.setGameSceneCanvasVisible( name === Screens.GAME );

    const ui_canvases = this.canvases[name];

    if ( ui_canvases )
    {
      for ( const canvas of ui_canvases )
      {
        Game.instance.allocateScreenCanvas( canvas );
        canvas.style.display = "block";
      }
    }

    this.current_screen = screen ?? null;
    this.current_ui = ui ?? null;
    this.current_screen_name = name;
    this.notifyScreenChanged();
    void audioController.playMusic(
      name === Screens.GAME ? AudioClips.MUSIC_3 : AudioClips.MUSIC_2,
      true
    );
  }

  static update( dt: number )
  {
    if ( this.current_screen )
      this.current_screen.update( dt );
  }
}
